import { useEffect, useState } from 'react';
import { useLoadingStore } from '@stores/loadingStore';

export function GlobalLoadingIndicator() {
  const { isLoading, loadingMessage } = useLoadingStore();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setVisible(true);
      setProgress(0);

      // Simulate progress
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            return prev;
          }
          // Slow down as we approach 90%
          const increment = Math.random() * (90 - prev) * 0.1;
          return prev + increment;
        });
      }, 200);

      return () => clearInterval(interval);
    } else {
      // Complete the progress bar before hiding
      setProgress(100);
      const timeout = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  if (!visible) {
    return null;
  }

  return (
    <>
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[100] h-1 bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full bg-primary-600 transition-all duration-200 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Optional loading message overlay */}
      {loadingMessage && (
        <div className="fixed inset-0 z-[99] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-lg px-6 py-4 flex items-center gap-3 dark:bg-gray-800">
            <div className="w-5 h-5 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            <span className="text-gray-700 dark:text-gray-200">{loadingMessage}</span>
          </div>
        </div>
      )}
    </>
  );
}

// Loading overlay component for specific sections
export function LoadingOverlay({
  isLoading,
  message,
  children,
}: {
  isLoading: boolean;
  message?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      {children}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg z-10 dark:bg-gray-800">
          <div className="text-center">
            <div className="w-8 h-8 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
            {message && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{message}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
